import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '@/components/Tooltip';

interface ChipProps extends PropsWithChildren {
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  className?: string;
  style?: 'default' | 'primary' | 'secondary' | 'error';
  /** Title to show in tooltip when content is truncated */
  title?: string;
}

const chipClasses = {
  default: 'bg-default-light text-default',
  primary: 'bg-brand/10 text-brand',
  secondary: 'bg-default text-default-dark',
  error: 'bg-error/10 text-error',
};

export const Chip = ({
  onClick,
  children,
  className,
  style = 'primary',
  title,
}: ChipProps) => {
  const [isOverflowed, setIsOverflowed] = useState(false);
  const chipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = chipRef.current;
    if (!element || !title) return;

    const checkOverflow = () => {
      const isOverflow = element.scrollWidth > element.clientWidth;
      setIsOverflowed(prev => (prev !== isOverflow ? isOverflow : prev));
    };

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);
    checkOverflow(); // Initial check

    return () => resizeObserver.disconnect();
  }, [title]);

  const chip = (
    <div
      ref={chipRef}
      className={twMerge(
        'rounded-[2px] px-2 py-1 text-center text-xs',
        title && 'truncate',
        chipClasses[style],
        className,
        onClick && 'cursor-pointer'
      )}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </div>
  );

  if (title && isOverflowed) {
    return (
      <Tooltip title={title} tooltipClassName="break-words">
        {chip}
      </Tooltip>
    );
  }

  return chip;
};
